/* istanbul ignore file */
/* tslint:disable */
/* eslint-disable */
import type { AdjustmentResults } from '../models/AdjustmentResults';
import type { CreateRegistrationWaiverRequest } from '../models/CreateRegistrationWaiverRequest';
import type { FieldResult } from '../models/FieldResult';
import type { FieldsUpdated } from '../models/FieldsUpdated';
import type { OpenApiInteger } from '../models/OpenApiInteger';
import type { Registration } from '../models/Registration';
import type { RegistrationCreateRequest } from '../models/RegistrationCreateRequest';
import type { RegistrationWaiver } from '../models/RegistrationWaiver';
import type { RegistrationWithDiscounts } from '../models/RegistrationWithDiscounts';
import type { SaveAdjustmentResultsRequest } from '../models/SaveAdjustmentResultsRequest';
import type { SaveFieldResultsRequest } from '../models/SaveFieldResultsRequest';
import type { SignedUploadUrl } from '../models/SignedUploadUrl';
import type { SignedUrl } from '../models/SignedUrl';
import type { UpdateRegistrationRequest } from '../models/UpdateRegistrationRequest';
import type { UpdateRegistrationWaiverRequest } from '../models/UpdateRegistrationWaiverRequest';

import type { CancelablePromise } from '../core/CancelablePromise';
import { OpenAPI } from '../core/OpenAPI';
import { request as __request } from '../core/request';

export class RegistrationsService {

    /**
     * @param formId
     * @param couponCodes
     * @param status
     * @returns RegistrationWithDiscounts Success
     * @throws ApiError
     */
    public static getRegistrationsWithDiscounts(
        formId?: OpenApiInteger,
        couponCodes?: string,
        status?: string,
    ): CancelablePromise<RegistrationWithDiscounts> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/registrations-with-discounts',
            query: {
                'formId': formId,
                'couponCodes': couponCodes,
                'status': status,
            },
        });
    }

    /**
     * @param requestBody
     * @returns Registration Success
     * @throws ApiError
     */
    public static createRegistration(
        requestBody: RegistrationCreateRequest,
    ): CancelablePromise<Registration> {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/registrations',
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                400: `Validation Error`,
            },
        });
    }

    /**
     * @param status
     * @returns Registration Success
     * @throws ApiError
     */
    public static getRegistrations(
        status?: string,
    ): CancelablePromise<Array<Registration>> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/registrations',
            query: {
                'status': status,
            },
        });
    }

    /**
     * @param registrationId
     * @param formWaiverResultId
     * @param requestBody
     * @returns RegistrationWaiver Success
     * @throws ApiError
     */
    public static updateRegistrationWaiver(
        registrationId: OpenApiInteger,
        formWaiverResultId: OpenApiInteger,
        requestBody: UpdateRegistrationWaiverRequest,
    ): CancelablePromise<RegistrationWaiver> {
        return __request(OpenAPI, {
            method: 'PATCH',
            url: '/registrations/{registrationId}/waivers/{formWaiverResultId}',
            path: {
                'registrationId': registrationId,
                'formWaiverResultId': formWaiverResultId,
            },
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * @param registrationId
     * @returns RegistrationWaiver Success
     * @throws ApiError
     */
    public static getWaiverResults(
        registrationId: OpenApiInteger,
    ): CancelablePromise<Array<RegistrationWaiver>> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/registrations/{registrationId}/waivers',
            path: {
                'registrationId': registrationId,
            },
        });
    }

    /**
     * @param registrationId
     * @param requestBody
     * @returns RegistrationWaiver Success
     * @throws ApiError
     */
    public static createRegistrationWaiver(
        registrationId: OpenApiInteger,
        requestBody: CreateRegistrationWaiverRequest,
    ): CancelablePromise<RegistrationWaiver> {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/registrations/{registrationId}/waivers',
            path: {
                'registrationId': registrationId,
            },
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * @param registrationId
     * @param formFieldId
     * @param fileExtension
     * @returns SignedUploadUrl Success
     * @throws ApiError
     */
    public static getFieldSignupUploadUrl(
        registrationId: OpenApiInteger,
        formFieldId: OpenApiInteger,
        fileExtension: string,
    ): CancelablePromise<SignedUploadUrl> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/registrations/{registrationId}/fields/{formFieldId}/upload-url',
            path: {
                'registrationId': registrationId,
                'formFieldId': formFieldId,
            },
            query: {
                'fileExtension': fileExtension,
            },
        });
    }

    /**
     * @param registrationId
     * @param formFieldId
     * @returns SignedUrl Success
     * @throws ApiError
     */
    public static getFieldSignupDownloadUrl(
        registrationId: OpenApiInteger,
        formFieldId: OpenApiInteger,
    ): CancelablePromise<SignedUrl> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/registrations/{registrationId}/fields/{formFieldId}/download-url',
            path: {
                'registrationId': registrationId,
                'formFieldId': formFieldId,
            },
        });
    }

    /**
     * @param registrationId
     * @returns FieldResult Success
     * @throws ApiError
     */
    public static getFieldResults(
        registrationId: OpenApiInteger,
    ): CancelablePromise<Array<FieldResult>> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/registrations/{registrationId}/fields',
            path: {
                'registrationId': registrationId,
            },
        });
    }

    /**
     * @param registrationId
     * @param requestBody
     * @returns FieldsUpdated Success
     * @throws ApiError
     */
    public static saveFieldResults(
        registrationId: OpenApiInteger,
        requestBody: SaveFieldResultsRequest,
    ): CancelablePromise<FieldsUpdated> {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/registrations/{registrationId}/fields',
            path: {
                'registrationId': registrationId,
            },
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * @param registrationId
     * @returns AdjustmentResults Success
     * @throws ApiError
     */
    public static getAdjustmentResults(
        registrationId: OpenApiInteger,
    ): CancelablePromise<AdjustmentResults> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/registrations/{registrationId}/adjustments',
            path: {
                'registrationId': registrationId,
            },
        });
    }

    /**
     * @param registrationId
     * @param requestBody
     * @returns AdjustmentResults Success
     * @throws ApiError
     */
    public static saveAdjustmentResults(
        registrationId: OpenApiInteger,
        requestBody: SaveAdjustmentResultsRequest,
    ): CancelablePromise<AdjustmentResults> {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/registrations/{registrationId}/adjustments',
            path: {
                'registrationId': registrationId,
            },
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * @param registrationId
     * @returns Registration Success
     * @throws ApiError
     */
    public static addToWaitlist(
        registrationId: OpenApiInteger,
    ): CancelablePromise<Registration> {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/registrations/{registrationId}/add-to-waitlist',
            path: {
                'registrationId': registrationId,
            },
        });
    }

    /**
     * @param registrationId
     * @param requestBody
     * @returns Registration Success
     * @throws ApiError
     */
    public static updateRegistration(
        registrationId: OpenApiInteger,
        requestBody: UpdateRegistrationRequest,
    ): CancelablePromise<Registration> {
        return __request(OpenAPI, {
            method: 'PATCH',
            url: '/registrations/{registrationId}',
            path: {
                'registrationId': registrationId,
            },
            body: requestBody,
            mediaType: 'application/json',
        });
    }

}
